/**
 * Invoices Management Page
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Search, Refresh, Print, Visibility, AssignmentReturn, Block } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import { ListToolbar, RowAction, humanize } from '../components/common/ListParts';
import { API_ENDPOINTS } from '../config/api';
import { Invoice, PaginatedResponse, ReturnableSale } from '../types';
import toast from 'react-hot-toast';
import { InvoicePrintData, fetchInvoicePrintData, printReceipt } from '../services/receipt';
import SaleReturnDialog, { SaleActionMode } from '../components/sales/SaleReturnDialog';

const money = (n: number) => Number(n || 0).toFixed(2);
const utc = (value: string) => dayjs(value.endsWith('Z') ? value : `${value}Z`);

const STATUS_CHIPS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  completed: { label: 'Completed', color: 'success' },
  refunded: { label: 'Returned', color: 'warning' },
  cancelled: { label: 'Voided', color: 'error' },
};

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoicePrintData | null>(null);
  const [selectedSale, setSelectedSale] = useState<ReturnableSale | null>(null);
  const [actionSaleId, setActionSaleId] = useState<number | null>(null);
  const [actionMode, setActionMode] = useState<SaleActionMode>('return');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<PaginatedResponse<Invoice>>(API_ENDPOINTS.INVOICES, {
        params: { page: page + 1, page_size: pageSize, search,
          from_date: fromDate?.format('YYYY-MM-DD'), to_date: toDate?.format('YYYY-MM-DD') }
      });
      setInvoices(response.data.items);
      setTotal(response.data.total);
    } catch (error) { toast.error('Failed to fetch invoices'); }
    finally { setLoading(false); }
  }, [page, pageSize, search, fromDate, toDate]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleView = async (invoice: Invoice) => {
    try {
      const [printData, saleInfo] = await Promise.all([
        fetchInvoicePrintData(invoice.id),
        api.get<ReturnableSale>(`${API_ENDPOINTS.SALES}/${invoice.sale_id}/returnable`).then((r) => r.data),
      ]);
      setSelectedInvoice(printData);
      setSelectedSale(saleInfo);
      setViewOpen(true);
    } catch (error) { toast.error('Failed to fetch invoice details'); }
  };

  const openAction = (saleId: number, mode: SaleActionMode) => {
    setViewOpen(false);
    setActionMode(mode);
    setActionSaleId(saleId);
  };

  const closeAction = useCallback(() => setActionSaleId(null), []);

  const handleActionDone = () => {
    setActionSaleId(null);
    fetchInvoices();
  };

  const handlePrint = async (invoiceId: number) => {
    try {
      await printReceipt(invoiceId);
    } catch (error) { toast.error('Failed to print invoice'); }
  };

  const columns: GridColDef[] = [
    { field: 'invoice_no', headerName: 'Invoice No', width: 130 },
    { field: 'invoice_date', headerName: 'Date', width: 160, renderCell: (params: GridRenderCellParams) =>
      params.value ? dayjs(params.value + (String(params.value).endsWith('Z') ? '' : 'Z')).format('DD/MM/YYYY hh:mm A') : '' },
    { field: 'customer_name', headerName: 'Customer', flex: 1, minWidth: 150 },
    { field: 'customer_phone', headerName: 'Mobile', width: 120 },
    { field: 'total_amount', headerName: 'Amount', width: 120, type: 'number',
      renderCell: (params: GridRenderCellParams) => <strong>₹{money(params.value)}</strong> },
    { field: 'sale_status', headerName: 'Status', width: 120, renderCell: (params: GridRenderCellParams) => {
      const chip = STATUS_CHIPS[params.value] || { label: humanize(params.value) || '-', color: 'default' as const };
      return <Chip size="small" label={chip.label} color={chip.color} />;
    }},
    { field: 'actions', headerName: '', width: 160, sortable: false, align: 'right', renderCell: (params: GridRenderCellParams) => (
      <>
        <RowAction title="View" icon={<Visibility fontSize="small" />} onClick={() => handleView(params.row)} />
        <RowAction title="Print" icon={<Print fontSize="small" />} onClick={() => handlePrint(params.row.id)} />
        <RowAction title="Return items" icon={<AssignmentReturn fontSize="small" />}
          disabled={params.row.sale_status === 'cancelled' || params.row.sale_status === 'refunded'}
          onClick={() => openAction(params.row.sale_id, 'return')} />
        <RowAction title="Void sale" icon={<Block fontSize="small" />} color="error"
          disabled={params.row.sale_status === 'cancelled'}
          onClick={() => openAction(params.row.sale_id, 'void')} />
      </>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Invoices" actions={<RowAction title="Refresh" icon={<Refresh />} onClick={fetchInvoices} />} />
      <Card>
        <ListToolbar>
          <TextField placeholder="Search invoice no, customer or mobile..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
          <DatePicker label="From" value={fromDate} onChange={setFromDate} sx={{ width: { xs: '100%', sm: 180 } }} />
          <DatePicker label="To" value={toDate} onChange={setToDate} sx={{ width: { xs: '100%', sm: 180 } }} />
        </ListToolbar>
        <DataGrid rows={invoices} columns={columns} loading={loading} rowCount={total}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }}
          pageSizeOptions={[10, 25, 50]} autoHeight disableRowSelectionOnClick />
      </Card>
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invoice: {selectedInvoice?.invoice_no}</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box>
              <Box
                sx={{
                  display: 'grid', gap: 1.5, mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'background.subtle',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                }}
              >
                {[
                  { label: 'Date', value: dayjs(selectedInvoice.invoice_date).format('DD/MM/YYYY hh:mm A') },
                  { label: 'Customer', value: selectedInvoice.customer_name || 'Walk-in' },
                  { label: 'Payment', value: String(selectedInvoice.payment_mode || '-').toUpperCase() },
                  ...(selectedInvoice.customer_gstin ? [{ label: 'Customer GSTIN', value: selectedInvoice.customer_gstin }] : []),
                  ...(selectedInvoice.place_of_supply ? [{ label: 'Place of Supply', value: selectedInvoice.place_of_supply }] : []),
                ].map((f) => (
                  <Box key={f.label} sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                    <Typography variant="body2" fontWeight={600} noWrap>{f.value}</Typography>
                  </Box>
                ))}
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow><TableCell>Item</TableCell><TableCell>HSN</TableCell><TableCell>GST%</TableCell><TableCell>Qty</TableCell><TableCell>Price</TableCell><TableCell>Total</TableCell></TableRow></TableHead>
                  <TableBody>
                    {selectedInvoice.items.map((item, i) => (
                      <TableRow key={i}><TableCell>{item.product_name}</TableCell><TableCell>{item.hsn_code}</TableCell>
                        <TableCell>{item.tax_percent}</TableCell>
                        <TableCell>{item.quantity}{item.unit_type && !Number.isInteger(item.quantity) ? ` ${item.unit_type}` : ''}</TableCell>
                        <TableCell>₹{money(item.unit_price)}</TableCell><TableCell>₹{money(item.line_total)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>GST Summary</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow><TableCell>HSN</TableCell><TableCell>GST%</TableCell><TableCell>Taxable</TableCell>
                    {selectedInvoice.is_interstate ? <TableCell>IGST</TableCell> : <><TableCell>CGST</TableCell><TableCell>SGST</TableCell></>}</TableRow></TableHead>
                  <TableBody>
                    {selectedInvoice.hsn_summary.map((h, i) => (
                      <TableRow key={i}><TableCell>{h.hsn_code || '-'}</TableCell><TableCell>{h.tax_percent}</TableCell><TableCell>₹{money(h.taxable_value)}</TableCell>
                        {selectedInvoice.is_interstate ? <TableCell>₹{money(h.igst_amount)}</TableCell>
                          : <><TableCell>₹{money(h.cgst_amount)}</TableCell><TableCell>₹{money(h.sgst_amount)}</TableCell></>}</TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography>Taxable Value: ₹{money(selectedInvoice.subtotal - selectedInvoice.discount_amount)}</Typography>
                {selectedInvoice.is_interstate
                  ? <Typography>IGST: ₹{money(selectedInvoice.igst_amount)}</Typography>
                  : <><Typography>CGST: ₹{money(selectedInvoice.cgst_amount)}</Typography><Typography>SGST: ₹{money(selectedInvoice.sgst_amount)}</Typography></>}
                <Typography variant="h6">Total: ₹{money(selectedInvoice.total_amount)}</Typography>
              </Box>
              {selectedSale?.status === 'cancelled' && (
                <Typography color="error" sx={{ mt: 2 }}>
                  <strong>Voided</strong>{selectedSale.voided_at && ` on ${utc(selectedSale.voided_at).format('DD/MM/YYYY hh:mm A')}`}
                  {selectedSale.void_reason && `: ${selectedSale.void_reason}`}
                </Typography>
              )}
              {!!selectedSale?.returns.length && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Returns</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Credit Note</TableCell><TableCell>Date</TableCell><TableCell>Items</TableCell>
                        <TableCell>By</TableCell><TableCell align="right">Refund</TableCell></TableRow></TableHead>
                      <TableBody>
                        {selectedSale.returns.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell>{r.return_no}{r.window_override && <Chip size="small" label="Override" sx={{ ml: 1 }} />}</TableCell>
                            <TableCell>{utc(r.return_date).format('DD/MM/YYYY')}</TableCell>
                            <TableCell>{r.items.map((i) => `${i.product_name} × ${Number(i.quantity)}`).join(', ')}</TableCell>
                            <TableCell>{r.created_by_name || '-'}</TableCell>
                            <TableCell align="right">₹{money(r.total_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography sx={{ mt: 1, textAlign: 'right' }}>Returned: ₹{money(selectedSale.returned_amount)}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
          {selectedSale && selectedSale.status !== 'cancelled' && (
            <Button color="error" startIcon={<Block />} onClick={() => openAction(selectedSale.sale_id, 'void')}>Void</Button>
          )}
          {selectedSale && selectedSale.status !== 'cancelled' && selectedSale.status !== 'refunded' && (
            <Button startIcon={<AssignmentReturn />} onClick={() => openAction(selectedSale.sale_id, 'return')}>Return</Button>
          )}
          <Button variant="contained" startIcon={<Print />} onClick={() => selectedInvoice && handlePrint(selectedInvoice.invoice_id)}>Print</Button>
        </DialogActions>
      </Dialog>
      <SaleReturnDialog saleId={actionSaleId} mode={actionMode} onClose={closeAction} onDone={handleActionDone} />
    </Box>
  );
};

export default Invoices;

